import { Injectable } from '@angular/core';

import {
  resolveXmlFieldDisplayKeyFromPersistedPath,
  stripPathPredicates,
} from '../xml/xml-field-key-map.flatten';
import { persistedXmlPathMatchesTemplate } from '../xml/xml-path-step-builder';
import xmlFieldKeyMapRaw from '../../../assets/rule-config/field-key-map-xml.json';
import {
  xmlFieldOptionDisplayLabel,
  type XmlFieldMapMeta,
  type XmlFieldOption,
} from '../models/field-option.model';
import { XmlFieldCatalogService } from './xml-field-catalog.service';
import { isHl7PersistedFieldValue } from '../utils/routing-conversion-type.util';

@Injectable()
export class XmlFieldPathResolverService {
  constructor(private readonly catalog: XmlFieldCatalogService) {}

  /**
   * Stored path → display field key (JSON `label`, plus values[] prefix when configured).
   */
  displayKeyFromPersistedPath(persisted: string): string {
    const raw = (persisted ?? '').toString().trim();
    if (!raw) {
      return '';
    }
    if (isHl7PersistedFieldValue(raw) && (raw.includes('/') || raw.includes('[@parentId'))) {
      return '';
    }
    const fromResolver = resolveXmlFieldDisplayKeyFromPersistedPath(xmlFieldKeyMapRaw, raw);
    if (fromResolver) {
      return fromResolver;
    }
    const opt = this.findOptionForPersistedPath(raw);
    if (opt) {
      return xmlFieldOptionDisplayLabel(opt);
    }
    const stripped = stripPathPredicates(raw);
    const fromStripped = this.catalog.findByPath(stripped);
    if (fromStripped) {
      return xmlFieldOptionDisplayLabel(fromStripped);
    }
    return raw;
  }

  findOptionForPersistedPath(persisted: string): XmlFieldOption | undefined {
    const raw = (persisted ?? '').toString().trim();
    if (!raw) {
      return undefined;
    }
    this.catalog.ensureLoaded();
    const exact = this.catalog.findByPath(raw);
    if (exact) {
      return exact;
    }
    const matches = this.catalog
      .getOptions()
      .filter((o) => persistedXmlPathMatchesTemplate(o.path, raw))
      .sort((a, b) => b.path.length - a.path.length);
    if (matches[0]) {
      return matches[0];
    }
    const cartesianKey = resolveXmlFieldDisplayKeyFromPersistedPath(xmlFieldKeyMapRaw, raw);
    if (cartesianKey) {
      return this.catalog.findByKey(cartesianKey);
    }
    return undefined;
  }

  metaForPersistedPath(persisted: string): XmlFieldMapMeta | null {
    const opt = this.findOptionForPersistedPath(persisted);
    if (opt) {
      return (
        this.catalog.getMetaByKey(opt.key) ?? {
          path: opt.path,
          isRepeatable: opt.isRepeatable,
          repeatableNodes: opt.repeatableNodes,
          nodePredicates: opt.nodePredicates,
        }
      );
    }
    return null;
  }
}
